/**
 * src/engine/sensitivity.ts — tornado sensitivity analysis (§6.17).
 *
 * For a chosen output metric, recompute it at low/high bounds of each input (±20%
 * relative for amounts/horizon; sensible absolute steps for rates) holding others fixed.
 * Bars are sorted by swing magnitude (largest first) for a tornado chart.
 */
import type { ScenarioInputs, TornadoBar } from '@/types';
import { project } from './index';

export type SensitivityMetric =
  | 'total_interest'
  | 'terminal_net_worth'
  | 'worst_ltv'
  | 'heirs_difference';

export type SensitivityInput =
  | 'monthly_draw'
  | 'portfolio_return'
  | 'base_rate'
  | 'advance_rate'
  | 'cap_gains_rate'
  | 'horizon';

const INPUT_LABELS: Record<SensitivityInput, string> = {
  monthly_draw: 'Monthly draw',
  portfolio_return: 'Portfolio return',
  base_rate: 'Base SOFR',
  advance_rate: 'Advance rate',
  cap_gains_rate: 'Capital-gains rate',
  horizon: 'Horizon (years)',
};

function metricOf(
  inputs: ScenarioInputs,
  metric: SensitivityMetric,
): number {
  const r = project(inputs);
  switch (metric) {
    case 'total_interest':
      return r.summary.totalInterest;
    case 'terminal_net_worth':
      return r.summary.endingNetWorth;
    case 'worst_ltv':
      return r.summary.worstLtv;
    case 'heirs_difference':
      return r.estate.difference;
  }
}

/** Produce the [lowInputs, highInputs] perturbation for an input dimension. */
function perturb(inputs: ScenarioInputs, input: SensitivityInput): [ScenarioInputs, ScenarioInputs] {
  const clone = (over: Partial<ScenarioInputs>): ScenarioInputs => ({ ...inputs, ...over });
  switch (input) {
    case 'monthly_draw':
      return [clone({ drawAmount: inputs.drawAmount * 0.8 }), clone({ drawAmount: inputs.drawAmount * 1.2 })];
    case 'portfolio_return':
      return [
        clone({ portfolioExpectedReturnAnnualPct: inputs.portfolioExpectedReturnAnnualPct - 2 }),
        clone({ portfolioExpectedReturnAnnualPct: inputs.portfolioExpectedReturnAnnualPct + 2 }),
      ];
    case 'base_rate':
      return [
        clone({ ratePath: { ...inputs.ratePath, baseRatePct: inputs.ratePath.baseRatePct - 1 } }),
        clone({ ratePath: { ...inputs.ratePath, baseRatePct: inputs.ratePath.baseRatePct + 1 } }),
      ];
    case 'advance_rate':
      return [
        clone({ blendedAdvanceRate: Math.max(0, inputs.blendedAdvanceRate - 0.05) }),
        clone({ blendedAdvanceRate: Math.min(1, inputs.blendedAdvanceRate + 0.05) }),
      ];
    case 'cap_gains_rate':
      return [
        clone({ tax: { ...inputs.tax, longTermCapGainsRatePct: Math.max(0, inputs.tax.longTermCapGainsRatePct - 5) } }),
        clone({ tax: { ...inputs.tax, longTermCapGainsRatePct: Math.min(100, inputs.tax.longTermCapGainsRatePct + 5) } }),
      ];
    case 'horizon':
      return [
        clone({ years: Math.max(1, Math.round(inputs.years * 0.8)) }),
        clone({ years: Math.min(40, Math.round(inputs.years * 1.2)) }),
      ];
  }
}

/** Compute sorted tornado bars for the chosen metric across the given inputs (§6.17). */
export function tornado(
  inputs: ScenarioInputs,
  metric: SensitivityMetric,
  dimensions: SensitivityInput[],
): TornadoBar[] {
  const base = metricOf(inputs, metric);
  const bars: TornadoBar[] = dimensions.map((dim) => {
    const [lo, hi] = perturb(inputs, dim);
    return {
      input: INPUT_LABELS[dim],
      low: metricOf(lo, metric),
      high: metricOf(hi, metric),
      base,
    };
  });
  bars.sort((a, b) => Math.abs(b.high - b.low) - Math.abs(a.high - a.low));
  return bars;
}
